import { styled } from '@mui/material/styles';
import { Link, Stack, Typography } from '@mui/material';
import { HeaderValidation } from '../../elements/HeaderValidation';
import { useInjection } from './hook';
import React, { memo } from 'react';
import { TableCustom } from 'app/components/elements/TableCustom';
import { HEADER_ASSET_MODAL_TAB } from '../../../const';
import { PagingInfo } from 'types/paging';
import { AssetModel } from '../../../type';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import { ModalAddNewModel } from './ModalAddNewModel';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';

type RenderDataMapProps = {
  dataAssetModel: AssetModel[];
  onClickEditUser: (assetModel: AssetModel) => void;
  onClickDeleteUser: (assetModel: AssetModel) => void;
  onClickViewUser: (assetModel: AssetModel) => void;
};
const renderDataAssetModel = ({
  dataAssetModel,
  onClickEditUser,
  onClickDeleteUser,
  onClickViewUser
}: RenderDataMapProps) => {
  return dataAssetModel.map((data) => {
    return {
      name: {
        value: <TextEllipsis text={data?.name} width={'95%'} />
      },
      manufacture: {
        value: <TextEllipsis text={data?.manufacturer?.name} width={'95%'} />
      },
      type: {
        value: <Typography>{data?.type?.name}</Typography>
      },
      files: {
        value: (
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
            {data.files.length !== 0 && (
              <Link
                style={{ fontWeight: 600, cursor: 'pointer' }}
                onClick={() => {
                  onClickViewUser(data);
                }}>
                View
              </Link>
            )}
          </Stack>
        )
      },
      actions: {
        value: (
          <Stack flexDirection={'row'} gap={'10px'} alignItems={'center'}>
            <img
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (data) {
                  onClickEditUser(data);
                }
              }}
              src={'/icons/icon_action_edit.svg'}
              alt={`icon_action_edit`}
            />
            <img
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (data) {
                  onClickDeleteUser(data);
                }
              }}
              src={'/icons/icon_action_delete.svg'}
              alt={`icon_action_delete`}
            />
          </Stack>
        )
      }
    };
  });
};

export type AssetModelTab = {
  pagingInfo: PagingInfo;
  handleChangePage: (value: number) => void;
  handleSort: (orderBy: string) => void;
  sortOption: { field?: string; order?: string };
  onSearch: (value: string) => void;
  dataAssetModel: AssetModel[];
  initSearchText: string;
  refreshDataModels: () => void;
  onExport: (type: string) => Promise<void>;
  refreshOptions?: () => Promise<void>;
};
const AssetModelTab = ({
  pagingInfo,
  handleChangePage,
  handleSort,
  sortOption,
  onSearch,
  dataAssetModel,
  initSearchText,
  refreshDataModels,
  onExport,
  refreshOptions
}: AssetModelTab) => {
  const {
    searchText,
    asyncAddNewModels,
    asyncEditNewModels,
    setSearchText,
    listButton,
    showModalAddNew,
    onCloseAddNew,
    showModalViewPdf,
    setShoModalViewPdf,
    modelEdit,
    showModalEditNew,
    setShowModalEditNew,
    onCloseEdit,
    setModelEdit,
    showModalDelete,
    setShowModalDelete,
    handleDeleteCreatedFile,
    asyncDeleter,
    optionTypes
  } = useInjection({ initSearchText, refreshDataModels, onExport, refreshOptions });
  return (
    <WrapperTab>
      <HeaderValidation
        searchText={searchText}
        buttons={listButton}
        setSearchText={setSearchText}
        onSearch={() => onSearch(searchText)}
      />
      <Stack sx={{ marginTop: '30px', marginBottom: '30px' }}>
        <TableCustom
          onSortHeader={handleSort}
          sort={sortOption}
          data={renderDataAssetModel({
            dataAssetModel,
            onClickEditUser: (assetModel: AssetModel) => {
              setShowModalEditNew(true);
              setModelEdit(assetModel);
            },
            onClickDeleteUser: (assetModel: AssetModel) => {
              setShowModalDelete(true);
              setModelEdit(assetModel);
            },
            onClickViewUser: (assetModel: AssetModel) => {
              setModelEdit(assetModel);
              setShoModalViewPdf(true);
            }
          })}
          headers={HEADER_ASSET_MODAL_TAB}
          handleChooseRow={() => {}}
          styledTableRow={{ cursor: ' default ' }}
          pagingInfo={pagingInfo}
          handleChangePage={handleChangePage}
        />
      </Stack>
      <ModalAddNewModel
        optionTypes={optionTypes}
        onSubmit={asyncAddNewModels}
        isOpen={showModalAddNew}
        onCloseModal={onCloseAddNew}
      />
      <ModalAddNewModel
        isEdit
        optionTypes={[
          { value: modelEdit?.type?.id.toString() || '', label: modelEdit?.type?.name || '' },
          ...optionTypes.filter((item) => item.value !== modelEdit?.type?.id.toString())
        ]}
        handleDeleteCreatedFile={handleDeleteCreatedFile}
        initData={modelEdit}
        onSubmit={asyncEditNewModels}
        isOpen={showModalEditNew}
        onCloseModal={onCloseEdit}
      />
      {modelEdit && (
        <DialogConfirm
          textTitle={'View Files'}
          isOpen={showModalViewPdf}
          onClose={() => setShoModalViewPdf(false)}>
          <Stack>
            <Stack flexDirection={'row'} flexWrap={'wrap'} gap={3}>
              {modelEdit.files.map((item, index) => (
                <Link key={index} href={item.url} target="_blank" rel="noopener noreferrer">
                  {item.storage.fileName}
                </Link>
              ))}
            </Stack>
          </Stack>
        </DialogConfirm>
      )}
      <DialogConfirm
        isOpen={showModalDelete}
        onClose={() => setShowModalDelete(false)}
        textTitle={'Delete Asset Model'}
        textButton={'Delete'}
        isDelete
        isIconClose
        textContent={'Are you sure you want to delete this asset model?'}
        onSubmit={asyncDeleter}
      />
    </WrapperTab>
  );
};

export const MemoAssetModelTab = memo(AssetModelTab);

const WrapperTab = styled(Stack)(() => ({
  width: '100%',
  marginTop: 20
}));
